import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useApi, useErrorMessage } from "../../api/context";
import type { WorkspaceSummary } from "../../api/types";
import { PageError, PageLoading } from "../../components/common";
import { pickWorkspace } from "../workspace-switcher/last-workspace";
import { CreateWorkspacePage } from "./CreateWorkspacePage";

/**
 * Where a signed-in user starts:
 * - `/onboarding` (after email verification / Google sign-in): no workspace yet → create one;
 *   otherwise → the wizard of the remembered (or first) workspace when the user owns it, else its
 *   dashboard (a staff member signing in must not land on the owner's setup wizard);
 * - `/`: no workspace → create one; otherwise → that workspace's dashboard.
 */
export function WorkspaceEntry({
  target,
  suffix = "",
}: {
  target: "onboarding" | "home";
  /** Path (+ query/hash) appended to `/w/<id>`, for workspace-less deep links. */
  suffix?: string;
}) {
  const api = useApi();
  const errorMessage = useErrorMessage();
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; workspaces: WorkspaceSummary[] }
    | { status: "error"; error: unknown }
  >({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      setState({ status: "ready", workspaces: await api.listWorkspaces() });
    } catch (error) {
      setState({ status: "error", error });
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") return <PageLoading />;
  if (state.status === "error") {
    return (
      <div className="mx-auto max-w-md px-6 py-10">
        <PageError message={errorMessage(state.error)} onRetry={() => void load()} />
      </div>
    );
  }
  const chosen = pickWorkspace(state.workspaces);
  if (!chosen) return <CreateWorkspacePage first />;
  const wizard = target === "onboarding" && chosen.isOwner;
  return (
    <Navigate to={wizard ? `/w/${chosen.id}/onboarding` : `/w/${chosen.id}${suffix}`} replace />
  );
}
